import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import storageManager from '../utils/storageManager.js';
import licenseManager from '../utils/licenseManager.js';
import migrateLegacyTrip from '../utils/migrateLegacyTrip.js';
import { cleanupLegacyTripData, syncUserSettingsFromActiveTrip } from '../utils/cleanupLegacyData.js';
import TripCard from './TripCard.jsx';
import StorageIndicator from './StorageIndicator.jsx';
import PremiumGate from './PremiumGate.jsx';
import TripFileSetup from './TripFileSetup.jsx';
import tripFileManager from '../utils/tripFileManager.js';
import { searchCities, searchCountries } from '../utils/countryUtils.js';

// Date field that always DISPLAYS European dd/mm/yyyy regardless of browser
// locale, while keeping the value in ISO yyyy-mm-dd and the native calendar.
// (A bare <input type="date"> shows the browser-locale format, which we can't
// override in HTML — Chrome even ignores the `lang` attribute.) A transparent
// native date input sits on top to open the calendar; the visible text shows
// the European order.
function EuroDateInput({ value, onChange, onFocus }) {
  const display = value && /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? (() => { const [y, m, d] = value.split('-'); return `${d}/${m}/${y}`; })()
    : '';
  const inputStyle = {
    width: '100%', padding: '12px', fontSize: '16px',
    border: '2px solid #ddd', borderRadius: '8px',
    boxSizing: 'border-box', background: 'white'
  };
  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <input type="text" readOnly value={display} placeholder="DD/MM/AAAA" style={inputStyle} />
      <input
        type="date"
        value={value || ''}
        onFocus={onFocus}
        onClick={(e) => { try { e.target.showPicker(); } catch { /* not supported — falls back to default */ } }}
        onChange={(e) => onChange(e.target.value)}
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer', margin: 0 }}
      />
    </div>
  );
}

function MyTrips({ userData, onNavigate, onTripChange }) {
  const { t } = useTranslation();
  const [trips, setTrips] = useState([]);
  const [activeTrip, setActiveTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPremium, setIsPremium] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTrip, setEditingTrip] = useState(null);
  const [newTripData, setNewTripData] = useState({
    name: '',
    nationality: '',
    destination: '',
    country: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [countrySuggestions, setCountrySuggestions] = useState([]);
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);
  const [showCountrySuggestions, setShowCountrySuggestions] = useState(false);
  const [citiesIndex, setCitiesIndex] = useState(null);
  const [showTripFileSetup, setShowTripFileSetup] = useState(false);
  const [pendingTripForSetup, setPendingTripForSetup] = useState(null);

  useEffect(() => {
    checkPremiumAndLoad();
    loadCitiesIndex();
  }, []);

  // Load cities index for autocomplete
  const loadCitiesIndex = async () => {
    try {
      // Get current language from i18n (strip region, e.g. es-ES → es)
      const currentLang = (i18n.language || 'en').split('-')[0];

      // Try language-specific index first, fallback to English
      let response = await fetch(`./TRIP/${currentLang}/cities-index.json`);

      if (!response.ok && currentLang !== 'en') {
        console.warn(`⚠️ Language-specific index not found for '${currentLang}', falling back to English`);
        response = await fetch('./TRIP/en/cities-index.json');
      }

      if (response.ok) {
        const index = await response.json();
        setCitiesIndex(index);
        console.log(`✅ Loaded cities index (${currentLang}): ${index.totalCities} cities`);
      }
    } catch (error) {
      console.error('❌ Error loading cities index:', error);
    }
  };

  const checkPremiumAndLoad = async () => {
    try {
      // Free tier allows one trip; premium unlocks multiple. Always load the
      // trips list — the limit is enforced at creation time (handleCreateNewTrip),
      // not by blocking the whole page.
      const hasPremium = await licenseManager.hasFeatureAccess('multiple_trips');
      setIsPremium(hasPremium);
      await loadTrips();
    } catch (error) {
      console.error('[MyTrips] Failed to check premium:', error);
      setLoading(false);
    }
  };

  const loadTrips = async () => {
    try {
      // Initialize IndexedDB
      await storageManager.initialize();

      // One-time: tag any owner-less trips to the current account (idempotent),
      // then everything below is already scoped to this user.
      await storageManager.claimUntaggedTrips();

      // Recover trips_index if localStorage was cleared but IndexedDB has trips
      await storageManager.ensureTripsIndexSync();

      // Run migration if needed (converts old localStorage trip to IndexedDB)
      const migrationResult = await migrateLegacyTrip();
      if (migrationResult.migrated) {
        console.log('[MyTrips] ✅ Legacy trip migrated successfully!', migrationResult);
      }

      // Get all trips
      const allTrips = await storageManager.getAllTrips();
      console.log('[MyTrips] Loaded trips:', allTrips.length, allTrips);

      // Get active trip
      const active = await storageManager.getActiveTrip();
      console.log('[MyTrips] Active trip:', active);

      // One-time cleanup: Remove old localStorage trip data after migration
      if (!localStorage.getItem('legacy_cleanup_done')) {
        console.log('[MyTrips] Running one-time legacy data cleanup...');
        cleanupLegacyTripData();
        await syncUserSettingsFromActiveTrip();
        localStorage.setItem('legacy_cleanup_done', 'true');
      }

      setTrips(allTrips);
      setActiveTrip(active);
      setLoading(false);
    } catch (error) {
      console.error('[MyTrips] Failed to load trips:', error);
      setLoading(false);
      alert('Error loading trips. The app will continue to work normally.');
    }
  };

  const handleInputChange = (field, value) => {
    setNewTripData(prev => ({ ...prev, [field]: value }));

    if (!citiesIndex) return;

    // City autocomplete
    if (field === 'destination' && value.length > 0) {
      const filtered = citiesIndex.cities
        .filter(item => item.city.toLowerCase().includes(value.toLowerCase()))
        .slice(0, 5);
      setCitySuggestions(filtered);
      setShowCitySuggestions(true);

      // Auto-fill country if exact match
      const exactMatch = citiesIndex.cities.find(item =>
        item.city.toLowerCase() === value.toLowerCase()
      );
      if (exactMatch) {
        setNewTripData(prev => ({ ...prev, country: exactMatch.country }));
      }
    } else if (field === 'destination') {
      setShowCitySuggestions(false);
    }

    // Country autocomplete
    if (field === 'country' && value.length > 0) {
      const filtered = citiesIndex.countries
        .filter(country => country.name.toLowerCase().includes(value.toLowerCase()))
        .slice(0, 5)
        .map(country => country.name);
      setCountrySuggestions(filtered);
      setShowCountrySuggestions(true);
    } else if (field === 'country') {
      setShowCountrySuggestions(false);
    }
  };

  // Prefill name + nationality so they don't have to be retyped for every trip.
  // Source it from the LOGGED-IN ACCOUNT's profile (set by login/register) — this
  // is correct per-account (no leaking another user's name) — and fall back to
  // this account's most recent trip.
  const getRememberedTraveller = () => {
    try {
      const profile = JSON.parse(localStorage.getItem('user_profile') || 'null');
      if (profile && (profile.name || profile.nationality)) {
        return { name: profile.name || '', nationality: profile.nationality || '' };
      }
    } catch { /* ignore */ }
    const fromTrip = [...trips]
      .sort((a, b) => new Date(b.createdAt || b.dates?.startDate || 0) - new Date(a.createdAt || a.dates?.startDate || 0))
      .map(t => t.traveler)
      .find(tr => tr && (tr.name || tr.nationality));
    return fromTrip || { name: '', nationality: '' };
  };

  const handleCreateNewTrip = () => {
    // Free tier: one trip max. Creating a second trip requires premium.
    if (!isPremium && trips.length >= 1) {
      setShowPaywall(true);
      return;
    }
    const remembered = getRememberedTraveller();
    setEditingTrip(null);
    setNewTripData({
      name: remembered.name || '',
      nationality: remembered.nationality || '',
      destination: '',
      country: '',
      startDate: new Date().toISOString().split('T')[0],
      endDate: new Date().toISOString().split('T')[0]
    });
    setShowCreateModal(true);
  };

  const handleEditTrip = (trip) => {
    setEditingTrip(trip);
    setNewTripData({
      name: trip.traveler?.name || '',
      nationality: trip.traveler?.nationality || '',
      destination: trip.destination?.city || '',
      country: trip.destination?.country || '',
      startDate: trip.dates?.startDate || new Date().toISOString().split('T')[0],
      endDate: trip.dates?.endDate || new Date().toISOString().split('T')[0]
    });
    setShowCreateModal(true);
  };

  const handleCreateTrip = async () => {
    if (!newTripData.name || !newTripData.nationality || !newTripData.destination || !newTripData.country) {
      alert('Please fill in all fields');
      return;
    }

    try {
      // Calculate total days
      const start = new Date(newTripData.startDate);
      const end = new Date(newTripData.endDate);
      const totalDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;

      if (editingTrip) {
        // EDIT MODE: Update existing trip
        const updatedTrip = {
          ...editingTrip,
          traveler: {
            name: newTripData.name,
            nationality: newTripData.nationality
          },
          destination: {
            city: newTripData.destination,
            country: newTripData.country
          },
          dates: {
            ...editingTrip.dates,
            startDate: newTripData.startDate,
            endDate: newTripData.endDate,
            totalDays: totalDays > 0 ? totalDays : 1
          },
          updatedAt: new Date().toISOString()
        };

        console.log('[MyTrips] Updating trip:', updatedTrip);
        await storageManager.saveTrip(updatedTrip);

        // Update localStorage userSettings if this is the active trip
        if (editingTrip.tripId === activeTrip?.tripId) {
          const userSettings = {
            userName: newTripData.name,
            userNationality: newTripData.nationality,
            destination: newTripData.destination,
            location: newTripData.destination,
            country: newTripData.country,
            startDate: newTripData.startDate,
            endDate: newTripData.endDate
          };
          localStorage.setItem('userSettings', JSON.stringify(userSettings));

          // Notify parent of change
          if (onTripChange) {
            onTripChange(updatedTrip);
          }
        }

        await loadTrips();
        setShowCreateModal(false);
        alert('✅ Trip updated successfully!');
      } else {
        // CREATE MODE: Build new trip object
        const newTrip = {
          tripId: storageManager.generateTripId(),
          traveler: {
            name: newTripData.name,
            nationality: newTripData.nationality
          },
          destination: {
            city: newTripData.destination,
            country: newTripData.country
          },
          dates: {
            startDate: newTripData.startDate,
            endDate: newTripData.endDate,
            currentDay: 1,
            totalDays: totalDays > 0 ? totalDays : 1
          },
          status: 'active',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };

        console.log('[MyTrips] Creating new trip:', newTrip);

        // Archive current active trip
        if (activeTrip) {
          await storageManager.updateTripStatus(activeTrip.tripId, 'completed');
        }

        // Save trip to IndexedDB
        await storageManager.saveTrip(newTrip);
        console.log('[MyTrips] Trip saved to IndexedDB');

        // Update trips index in localStorage
        const index = storageManager.getTripsIndex();
        index.activeTrip = newTrip.tripId;
        if (!index.tripIds.includes(newTrip.tripId)) {
          index.tripIds.push(newTrip.tripId);
        }
        index.lastModified = new Date().toISOString();
        storageManager.saveTripsIndex(index);
        console.log('[MyTrips] Trips index updated:', index);

        // Update localStorage userSettings for active trip
        const userSettings = {
          userName: newTripData.name,
          userNationality: newTripData.nationality,
          destination: newTripData.destination,
          location: newTripData.destination,
          country: newTripData.country,
          startDate: newTripData.startDate,
          endDate: newTripData.endDate
        };
        localStorage.setItem('userSettings', JSON.stringify(userSettings));

        // Reload trips
        await loadTrips();

        // Notify parent
        if (onTripChange) {
          onTripChange(newTrip);
        }

        setShowCreateModal(false);
        setNewTripData({
          name: '',
          nationality: '',
          destination: '',
          country: '',
          startDate: new Date().toISOString().split('T')[0],
          endDate: new Date().toISOString().split('T')[0]
        });

        // Show auto-save setup modal (only for Chrome/Edge)
        if (tripFileManager.isSupported()) {
          setPendingTripForSetup(newTrip);
          setShowTripFileSetup(true);
        } else {
          alert('🎉 New trip created successfully!');
        }
      }
    } catch (error) {
      console.error('[MyTrips] Failed to create trip:', error);
      alert('Error creating trip. Please try again.');
    }
  };

  const handleSwitchTrip = async (trip) => {
    try {
      console.log('[MyTrips] handleSwitchTrip called with:', trip);
      console.log('[MyTrips] Current activeTrip:', activeTrip);

      if (trip.tripId === activeTrip?.tripId) {
        console.log('[MyTrips] Trip is already active, skipping');
        return;
      }

      // Only ask for confirmation when there's a current trip being replaced
      // (switching archives it). Activating from "no active trip" has nothing to
      // lose, so go straight in — fewer clicks.
      if (activeTrip) {
        const confirmed = window.confirm(
          `Switch to "${trip.destination?.city}"?\n\nYour current trip will be saved automatically.`
        );
        if (!confirmed) {
          console.log('[MyTrips] User cancelled switch');
          return;
        }
      }

      console.log('[MyTrips] Switching trip...');

      // Archive current active trip
      if (activeTrip) {
        console.log('[MyTrips] Archiving current active trip:', activeTrip.tripId);
        await storageManager.updateTripStatus(activeTrip.tripId, 'completed');
      }

      // Activate new trip
      console.log('[MyTrips] Activating new trip:', trip.tripId);
      await storageManager.updateTripStatus(trip.tripId, 'active');

      // Update trips index
      const index = storageManager.getTripsIndex();
      index.activeTrip = trip.tripId;
      index.lastModified = new Date().toISOString();
      storageManager.saveTripsIndex(index);
      console.log('[MyTrips] Updated trips index:', index);

      // Reload trips
      await loadTrips();

      // Notify parent
      if (onTripChange) {
        const activatedTrip = await storageManager.getTrip(trip.tripId);
        console.log('[MyTrips] Notifying parent with trip:', activatedTrip);
        onTripChange(activatedTrip);
      }

      // Go straight into the trip — no "Switched!" alert, no extra "Continue" tap.
      if (onNavigate) onNavigate('home');
    } catch (error) {
      console.error('[MyTrips] Failed to switch trip:', error);
      alert('Error switching trip. Please try again.');
    }
  };

  const handleCompleteTrip = async (trip) => {
    try {
      const confirmed = window.confirm(
        `Mark "${trip.destination?.city}" as completed?\n\nYou can reactivate it later.`
      );
      if (!confirmed) return;

      await storageManager.updateTripStatus(trip.tripId, 'completed');

      // If this was the active trip, clear active trip
      if (trip.tripId === activeTrip?.tripId) {
        const index = storageManager.getTripsIndex();
        index.activeTrip = null;
        storageManager.saveTripsIndex(index);

        if (onTripChange) {
          onTripChange(null);
        }
      }

      await loadTrips();
      alert('✓ Trip completed');
    } catch (error) {
      console.error('[MyTrips] Failed to complete trip:', error);
      alert('Error completing trip. Please try again.');
    }
  };

  const handleDeleteTrip = async (trip) => {
    try {
      const confirmed1 = window.confirm(
        `Delete "${trip.destination?.city}"?\n\nThis will delete all photos, notes, and data for this trip.`
      );
      if (!confirmed1) return;

      const confirmed2 = window.confirm(
        `Are you ABSOLUTELY sure?\n\nThis action cannot be undone!`
      );
      if (!confirmed2) return;

      await storageManager.deleteTrip(trip.tripId);

      // Update trips index
      const index = storageManager.getTripsIndex();
      index.tripIds = index.tripIds.filter(id => id !== trip.tripId);
      if (index.activeTrip === trip.tripId) {
        index.activeTrip = null;
        if (onTripChange) {
          onTripChange(null);
        }
      }
      storageManager.saveTripsIndex(index);

      await loadTrips();
      alert('✓ Trip deleted successfully');
    } catch (error) {
      console.error('[MyTrips] Failed to delete trip:', error);
      alert('Error deleting trip. Please try again.');
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f1eb 0%, #ede4d3 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center', color: '#8B7355' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
          <p>Loading your trips...</p>
        </div>
      </div>
    );
  }

  if (showPaywall) {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f5f1eb 0%, #ede4d3 100%)' }}>
        <div style={{
          background: 'white',
          padding: '20px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          display: 'flex',
          alignItems: 'center',
          gap: '15px',
          marginBottom: '20px'
        }}>
          <span onClick={() => onNavigate('home')} style={{ fontSize: '24px', cursor: 'pointer', color: '#8B7355' }}>←</span>
          <h1 style={{ fontSize: '20px', fontWeight: '900', color: '#8B7355', textTransform: 'uppercase', letterSpacing: '-0.5px', margin: 0 }}>My Trips</h1>
        </div>
        <PremiumGate feature="multiple_trips" onNavigate={() => setShowPaywall(false)} />
      </div>
    );
  }

  // Separate trips by status and date
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Start of today

  const activeTrips = trips.filter(t => t.status === 'active');
  const plannedTrips = trips.filter(t => t.status === 'planned');
  const completedTrips = trips.filter(t => t.status === 'completed' || t.status === 'archived');

  // Separate completed/archived trips into PAST (ended before today) and UPCOMING (end date is today or future)
  const pastTrips = completedTrips.filter(t => {
    if (!t.dates?.endDate) return true; // No date = assume past
    const endDate = new Date(t.dates.endDate);
    endDate.setHours(0, 0, 0, 0);
    return endDate < today;
  });

  const upcomingTrips = completedTrips.filter(t => {
    if (!t.dates?.endDate) return false; // No date = assume past
    const endDate = new Date(t.dates.endDate);
    endDate.setHours(0, 0, 0, 0);
    return endDate >= today;
  });

  // Show all active/planned trips if no activeTrip is set in localStorage
  const tripsToShow = activeTrip ? [] : [...activeTrips, ...plannedTrips];

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f5f1eb 0%, #ede4d3 100%)', paddingBottom: '40px' }}>
      {/* Header */}
      <div style={{
        background: 'white',
        padding: '20px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
        marginBottom: '20px'
      }}>
        <span onClick={() => onNavigate('home')} style={{ fontSize: '24px', cursor: 'pointer', color: '#8B7355' }}>←</span>
        <h1 style={{ fontSize: '20px', fontWeight: '900', color: '#8B7355', textTransform: 'uppercase', letterSpacing: '-0.5px', margin: 0, flex: 1 }}>{t('myTrips.title')}</h1>
        <button
          onClick={handleCreateNewTrip}
          style={{
            background: '#C4714A',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            padding: '10px 16px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          {t('myTrips.newTripButton')}
        </button>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
        {/* Active Trip (from localStorage index) */}
        {activeTrip && (
          <div style={{ marginBottom: '32px' }}>
            <div style={{
              fontSize: '11px',
              color: '#8B7355',
              fontWeight: '700',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              marginBottom: '12px'
            }}>
              {t('myTrips.activeTrip')}
            </div>
            <TripCard
              trip={activeTrip}
              isActive={true}
              onClick={() => onNavigate('home')}
              onComplete={() => handleCompleteTrip(activeTrip)}
              onDelete={() => handleDeleteTrip(activeTrip)}
              onEdit={() => handleEditTrip(activeTrip)}
            />
          </div>
        )}

        {/* Show all trips if no active trip set (recovery mode) */}
        {!activeTrip && tripsToShow.length > 0 && (
          <div style={{ marginBottom: '32px' }}>
            <div style={{
              fontSize: '11px',
              color: '#d97706',
              fontWeight: '700',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              marginBottom: '12px'
            }}>
              {t('myTrips.noActiveTrip')}
            </div>
            {tripsToShow.map(trip => (
              <div key={trip.tripId} style={{ marginBottom: '16px' }}>
                <TripCard
                  trip={trip}
                  isActive={false}
                  onSwitch={() => handleSwitchTrip(trip)}
                  onComplete={() => handleCompleteTrip(trip)}
                  onDelete={() => handleDeleteTrip(trip)}
                  onEdit={() => handleEditTrip(trip)}
                />
              </div>
            ))}
          </div>
        )}

        {/* No active trip */}
        {!activeTrip && trips.length > 0 && (
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            textAlign: 'center',
            marginBottom: '32px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <p style={{ color: '#666', marginBottom: '12px' }}>No active trip. Activate one below or create a new trip.</p>
          </div>
        )}

        {/* Upcoming Trips */}
        {upcomingTrips.length > 0 && (
          <div style={{ marginBottom: '32px' }}>
            <div style={{
              fontSize: '11px',
              color: '#3498DB',
              fontWeight: '700',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              marginBottom: '12px'
            }}>
              {t('myTrips.upcomingTrips')} ({upcomingTrips.length})
            </div>
            <div style={{ display: 'grid', gap: '16px' }}>
              {upcomingTrips.map(trip => (
                <TripCard
                  key={trip.tripId}
                  trip={trip}
                  isActive={false}
                  onSwitch={() => handleSwitchTrip(trip)}
                  onDelete={() => handleDeleteTrip(trip)}
                  onEdit={() => handleEditTrip(trip)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Past Trips */}
        {pastTrips.length > 0 && (
          <div>
            <div style={{
              fontSize: '11px',
              color: '#8B7355',
              fontWeight: '700',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              marginBottom: '12px'
            }}>
              {t('myTrips.pastTrips')} ({pastTrips.length})
            </div>
            <div style={{ display: 'grid', gap: '16px' }}>
              {pastTrips.map(trip => (
                <TripCard
                  key={trip.tripId}
                  trip={trip}
                  isActive={false}
                  onSwitch={() => handleSwitchTrip(trip)}
                  onDelete={() => handleDeleteTrip(trip)}
                  onEdit={() => handleEditTrip(trip)}
                />
              ))}
            </div>
          </div>
        )}

        {/* No trips at all */}
        {trips.length === 0 && (
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '48px 32px',
            textAlign: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontSize: '64px', marginBottom: '24px' }}>✈️</div>
            <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#333', marginBottom: '12px' }}>
              {t('myTrips.noTripsYet')}
            </h3>
            <p style={{ fontSize: '16px', color: '#666', marginBottom: '24px' }}>
              {t('myTrips.createFirstTrip')}
            </p>
            <button
              onClick={handleCreateNewTrip}
              style={{
                background: '#C4714A',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                padding: '14px 32px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(184, 160, 130, 0.3)'
              }}
            >
              {t('myTrips.createFirstTripButton')}
            </button>
          </div>
        )}
      </div>

      {/* Storage Indicator */}
      <div style={{ position: 'fixed', bottom: '20px', right: '20px' }}>
        <StorageIndicator />
      </div>

      {/* Create/Edit Trip Modal */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '20px',
          overflowY: 'auto'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '500px',
            width: '100%',
            boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#333', marginBottom: '24px' }}>
              {editingTrip ? t('myTrips.editTrip') : t('myTrips.createNewTrip')}
            </h2>

            {/* Name */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#666', marginBottom: '8px' }}>
                {t('myTrips.yourName')} *
              </label>
              <input
                type="text"
                value={newTripData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder={t('myTrips.placeholders.name')}
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '16px',
                  border: '2px solid #ddd',
                  borderRadius: '8px',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {/* Nationality */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#666', marginBottom: '8px' }}>
                {t('myTrips.nationality')} *
              </label>
              <input
                type="text"
                value={newTripData.nationality}
                onChange={(e) => handleInputChange('nationality', e.target.value)}
                placeholder={t('myTrips.placeholders.nationality')}
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '16px',
                  border: '2px solid #ddd',
                  borderRadius: '8px',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {/* Destination with autocomplete */}
            <div style={{ marginBottom: '20px', position: 'relative' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#666', marginBottom: '8px' }}>
                {t('myTrips.destination')} *
              </label>
              <input
                type="text"
                value={newTripData.destination}
                onChange={(e) => handleInputChange('destination', e.target.value)}
                onBlur={() => setTimeout(() => setShowCitySuggestions(false), 150)}
                placeholder={t('myTrips.placeholders.destination')}
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '16px',
                  border: '2px solid #ddd',
                  borderRadius: '8px',
                  boxSizing: 'border-box'
                }}
              />
              {showCitySuggestions && citySuggestions.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  background: 'white',
                  border: '2px solid #ddd',
                  borderTop: 'none',
                  borderRadius: '0 0 8px 8px',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  zIndex: 1000
                }}>
                  {citySuggestions.map((suggestion, index) => (
                    <div
                      key={index}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        setNewTripData({ ...newTripData, destination: suggestion.city, country: suggestion.country });
                        setShowCitySuggestions(false);
                      }}
                      style={{
                        padding: '12px',
                        cursor: 'pointer',
                        borderBottom: index < citySuggestions.length - 1 ? '1px solid #eee' : 'none',
                        fontSize: '14px'
                      }}
                      onMouseEnter={(e) => e.target.style.background = '#f5f5f5'}
                      onMouseLeave={(e) => e.target.style.background = 'white'}
                    >
                      {suggestion.city}, {suggestion.country}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Country with autocomplete */}
            <div style={{ marginBottom: '20px', position: 'relative' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#666', marginBottom: '8px' }}>
                {t('myTrips.country')} *
              </label>
              <input
                type="text"
                value={newTripData.country}
                onChange={(e) => handleInputChange('country', e.target.value)}
                onBlur={() => setTimeout(() => setShowCountrySuggestions(false), 150)}
                placeholder={t('myTrips.placeholders.country')}
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '16px',
                  border: '2px solid #ddd',
                  borderRadius: '8px',
                  boxSizing: 'border-box'
                }}
              />
              {showCountrySuggestions && countrySuggestions.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  background: 'white',
                  border: '2px solid #ddd',
                  borderTop: 'none',
                  borderRadius: '0 0 8px 8px',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  zIndex: 1000
                }}>
                  {countrySuggestions.map((country, index) => (
                    <div
                      key={index}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        setNewTripData({ ...newTripData, country });
                        setShowCountrySuggestions(false);
                      }}
                      style={{
                        padding: '12px',
                        cursor: 'pointer',
                        borderBottom: index < countrySuggestions.length - 1 ? '1px solid #eee' : 'none',
                        fontSize: '14px'
                      }}
                      onMouseEnter={(e) => e.target.style.background = '#f5f5f5'}
                      onMouseLeave={(e) => e.target.style.background = 'white'}
                    >
                      {country}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Dates */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#666', marginBottom: '8px' }}>
                  {t('myTrips.startDate')}
                </label>
                <EuroDateInput
                  value={newTripData.startDate}
                  onFocus={() => { setShowCitySuggestions(false); setShowCountrySuggestions(false); }}
                  onChange={(v) => handleInputChange('startDate', v)}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#666', marginBottom: '8px' }}>
                  {t('myTrips.endDate')}
                </label>
                <EuroDateInput
                  value={newTripData.endDate}
                  onFocus={() => { setShowCitySuggestions(false); setShowCountrySuggestions(false); }}
                  onChange={(v) => handleInputChange('endDate', v)}
                />
              </div>
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingTrip(null);
                }}
                style={{
                  flex: 1,
                  padding: '14px',
                  fontSize: '16px',
                  fontWeight: '600',
                  border: '2px solid #ddd',
                  borderRadius: '12px',
                  background: 'white',
                  color: '#666',
                  cursor: 'pointer'
                }}
              >
                {t('myTrips.cancel')}
              </button>
              <button
                onClick={handleCreateTrip}
                style={{
                  flex: 1,
                  padding: '14px',
                  fontSize: '16px',
                  fontWeight: '600',
                  border: 'none',
                  borderRadius: '12px',
                  background: '#C4714A',
                  color: 'white',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(184, 160, 130, 0.3)'
                }}
              >
                {editingTrip ? t('myTrips.saveChanges') : t('myTrips.createTrip')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Trip File Setup Modal */}
      {showTripFileSetup && pendingTripForSetup && (
        <TripFileSetup
          tripId={pendingTripForSetup.tripId}
          tripData={pendingTripForSetup}
          onComplete={(result) => {
            setShowTripFileSetup(false);
            setPendingTripForSetup(null);
            alert('🎉 Viaje creado y auto-guardado configurado!');
          }}
          onSkip={() => {
            setShowTripFileSetup(false);
            setPendingTripForSetup(null);
            alert('🎉 Nuevo viaje creado exitosamente!');
          }}
        />
      )}
    </div>
  );
}

export default MyTrips;
